const textFields = 'input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]):not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="color"]), textarea, [contenteditable="true"]';

export function dialogViewportBounds(view, viewport = view.visualViewport) {
  const height = viewport?.height || view.innerHeight;
  const layoutHeight = Math.max(view.innerHeight, view.document.documentElement.clientHeight);
  return {
    top: Math.max(0, viewport?.offsetTop || 0), height,
    // Pinch zoom also shrinks the visual viewport; it is not a keyboard.
    keyboard: layoutHeight - height * (viewport?.scale || 1) > 120,
  };
}

/** Keep every open dialog inside the visible area above a virtual keyboard. */
export class DialogViewport {
  constructor(root, viewport = root.ownerDocument.defaultView.visualViewport) {
    this.root = root; this.view = root.ownerDocument.defaultView;
    this.viewport = viewport;
    this.schedule = () => {
      if (!this.frame) this.frame = this.view.requestAnimationFrame(() => { this.frame = null; this.update(); });
    };
    this.observer = new this.view.MutationObserver(this.schedule);
    this.observer.observe(root, { subtree: true, attributes: true, attributeFilter: ['open'] });
    root.addEventListener('focusin', this.schedule);
    root.addEventListener('focusout', this.schedule);
    this.view.addEventListener('resize', this.schedule);
    this.viewport?.addEventListener('resize', this.schedule);
    this.viewport?.addEventListener('scroll', this.schedule);
    this.update();
  }
  update() {
    const { root, view } = this, bounds = dialogViewportBounds(view, this.viewport);
    root.style.setProperty('--dialog-viewport-top', `${bounds.top}px`);
    root.style.setProperty('--dialog-viewport-height', `${bounds.height}px`);
    root.toggleAttribute('data-input-dialogs-at-top', bounds.keyboard || view.innerWidth <= 900 || view.navigator.maxTouchPoints > 0 || view.matchMedia('(any-pointer: coarse)').matches);
    for (const dialog of root.querySelectorAll('dialog[open]')) {
      const fields = [...dialog.querySelectorAll(textFields)].filter(field => field.closest('dialog') === dialog && !field.disabled && field.getClientRects().length);
      dialog.classList.toggle('input-dialog', fields.length > 0);
    }
    const field = root.ownerDocument.activeElement, dialog = field?.closest('dialog[open]');
    if (!dialog || !root.contains(dialog) || !field.matches(textFields)) return;
    const box = dialog.getBoundingClientRect(), fieldBox = field.getBoundingClientRect();
    let top = Math.max(bounds.top, box.top) + 12;
    const bottom = Math.min(bounds.top + bounds.height, box.bottom) - 12;
    for (const heading of dialog.querySelectorAll('.dialog-top, .choice-heading, .bookmark-dialog-heading, .music-heading')) {
      if (heading.closest('dialog') === dialog && heading.getClientRects().length && view.getComputedStyle(heading).position === 'sticky') top = Math.max(top, heading.getBoundingClientRect().bottom + 8);
    }
    if (bottom <= top) return;
    if (fieldBox.top < top) dialog.scrollTop += fieldBox.top - top;
    else if (fieldBox.bottom > bottom) dialog.scrollTop += fieldBox.bottom - bottom;
  }
  destroy() {
    const { root, view } = this;
    this.observer.disconnect();
    if (this.frame) view.cancelAnimationFrame(this.frame);
    root.removeEventListener('focusin', this.schedule);
    root.removeEventListener('focusout', this.schedule);
    view.removeEventListener('resize', this.schedule);
    this.viewport?.removeEventListener('resize', this.schedule);
    this.viewport?.removeEventListener('scroll', this.schedule);
    root.style.removeProperty('--dialog-viewport-top');
    root.style.removeProperty('--dialog-viewport-height');
    root.removeAttribute('data-input-dialogs-at-top');
  }
}
